import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Timeline — "La nostra storia" (Figma 630-7960).
 *
 * Desktop (>=992px, matches _timeline.scss's media-breakpoint-up(lg)): pins
 * .js-timeline-viewport and fakes a horizontal scroll on .js-timeline-track
 * via the standard GSAP ScrollTrigger "containerAnimation" pattern, so each
 * chapter becomes .is-active as it crosses the center of the viewport. Once
 * the user stops scrolling, ScrollTrigger's `snap` eases the rest of the
 * way to whichever chapter is closest, so the view never settles mid-transition
 * between two chapters.
 *
 * Mobile and prefers-reduced-motion both fall through to the plain CSS
 * stacked list (see _timeline.scss) — no pin/horizontal-scroll JS runs for
 * either case, but the per-chapter "next" buttons still work everywhere via
 * a plain scrollIntoView() fallback.
 */
(function () {
  'use strict';

  gsap.registerPlugin(ScrollTrigger);

  var section = document.querySelector('.js-timeline');
  if (!section) return;

  var viewport = section.querySelector('.js-timeline-viewport');
  var track = section.querySelector('.js-timeline-track');
  // Array, not a NodeList — .map()/.forEach() are used on it below.
  var items = track ? Array.prototype.slice.call(track.querySelectorAll('.timeline__item')) : [];
  var nextButtons = section.querySelectorAll('.js-timeline-next');

  if (!viewport || !track || !items.length) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Populated only while the desktop pin (below) is active: for each item,
  // the containerAnimation progress (0-1) at which that chapter sits exactly
  // centered in the viewport. Used by both the scroll-snap and goToItem()
  // below, so "next chapter" and "settle here" always agree on the same
  // target.
  var activeCenterProgress = null;
  var activePin = null;

  function goToItem(index) {
    var targetItem = items[index];
    if (!targetItem) return;

    if (activePin && activeCenterProgress && activeCenterProgress[index] !== undefined) {
      var target = activePin.start + activeCenterProgress[index] * (activePin.end - activePin.start);
      window.scrollTo({ top: target, behavior: reducedMotion ? 'auto' : 'smooth' });
      return;
    }

    targetItem.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
  }

  nextButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var target = parseInt(btn.getAttribute('data-target-index'), 10);
      if (!isNaN(target)) goToItem(target);
    });
  });

  if (reducedMotion) return; // "next" buttons above still work via scrollIntoView

  var mm = gsap.matchMedia();

  mm.add('(min-width: 992px)', function () {
    var distance = track.scrollWidth - viewport.clientWidth;
    if (distance <= 0) return; // not enough content to justify horizontal scroll

    var scrollTween = gsap.to(track, {
      x: -distance,
      ease: 'none', // mandatory for scroll/position sync with containerAnimation
    });

    // Videos only ever play in the active chapter — off-screen (translated)
    // chapters are still present in the DOM the whole time on desktop, so
    // autoplay/browser visibility heuristics alone aren't reliable here.
    items.forEach(function (item, i) {
      var video = item.querySelector('.timeline__media-video');
      if (video && i !== 0) video.pause();
    });

    // The exact containerAnimation progress at which chapter i sits
    // perfectly centered in the viewport, derived geometrically (not from
    // ScrollTrigger's "left center"/"right center" keywords — verified
    // empirically that despite the name, the midpoint of that window is NOT
    // where the item is actually centered). The leading spacer is sized to
    // exactly (viewportWidth - itemWidth) / 2, which is precisely the shift
    // needed to center item 0 at progress 0 — every subsequent item needs
    // exactly one more itemWidth of shift than the one before it, so
    // progress simply scales with item index.
    var itemWidth = items[0].getBoundingClientRect().width;
    var centerProgress = items.map(function (_, i) {
      return (i * itemWidth) / distance;
    });

    // Whichever chapter's centerProgress is closest to the current scroll
    // progress is active — a nearest-neighbor split, not fixed dwell windows.
    // This guarantees full coverage (every progress value has exactly one
    // nearest chapter, so .is-active is never absent from all of them) and
    // keeps "active" perfectly in sync with the scroll-snap target: the
    // active chapter IS always the one a stopped scroll will settle on.
    function syncActiveChapter() {
      var progress = scrollTween.progress();
      var activeIndex = 0;
      var smallestDelta = Infinity;
      centerProgress.forEach(function (center, i) {
        var delta = Math.abs(progress - center);
        if (delta < smallestDelta) {
          smallestDelta = delta;
          activeIndex = i;
        }
      });

      items.forEach(function (item, i) {
        var isActive = i === activeIndex;
        if (isActive === item.classList.contains('is-active')) return;

        item.classList.toggle('is-active', isActive);
        var video = item.querySelector('.timeline__media-video');
        if (!video) return;
        if (isActive) {
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }

    var pin = ScrollTrigger.create({
      trigger: viewport,
      start: 'top top',
      end: '+=' + Math.round(distance * 1.15), // a bit more scroll "room" than 1:1 so motion isn't rushed
      pin: true,
      // Small numeric scrub (vs true): smooths discrete wheel/trackpad ticks into
      // continuous motion without a perceptible lag, so scrolling reads fluid, not steppy.
      scrub: 0.4,
      animation: scrollTween,
      invalidateOnRefresh: true,
      anticipatePin: 0.5, // seamless pin entry — no catch-up hitch when the section pins
      fastScrollEnd: true, // after a hard flick, settle promptly instead of drifting to catch up
      onUpdate: syncActiveChapter,
      // Once the user stops scrolling, ease to whichever chapter's centered
      // position is closest, rather than resting mid-transition. Tuned to assist
      // navigation, not dominate it: an ease-OUT settle (fast start, gentle finish)
      // removes the "resistance" of the old ease-in; momentum + direction awareness
      // keep it feeling like natural, user-driven scrolling.
      snap: {
        snapTo: centerProgress,
        duration: { min: 0.2, max: 0.45 }, // snappier settle — less "magnetic" domination
        delay: 0.1, // brief free-scroll window before snapping engages (not a grab)
        ease: 'power2.out',
        inertia: true, // a flick carries momentum toward the next chapter
        directional: true, // snap follows scroll direction — no backward yank
      },
    });

    activeCenterProgress = centerProgress;
    activePin = pin;

    // Runs automatically when matchMedia reverts (resize below 992px)
    return function () {
      activeCenterProgress = null;
      activePin = null;
      pin.kill();
      scrollTween.kill();
    };
  });

  window.addEventListener('load', function () {
    ScrollTrigger.refresh();
  });
})();
